// Thin wrapper over /api/studentelementnotes - the only backend endpoint
// that can create a TEACHER-authored note (the student platform's own
// PlatformNoteController hardcodes authorType=STUDENT and never touches
// teacherAuthor). Relation fields in request bodies are bare scalar ids
// (StudentDeserializer-style convention, confirmed against every other
// entity in this backend) - GET responses nest full objects since no
// entity here has a custom @JsonSerialize, only @JsonDeserialize.

import axios from 'axios';

const jsonHeaders = {
   withCredentials: true,
   headers: { 'Content-Type': 'application/json' },
};

// One call, every note for the whole workspace (every target kind at once) -
// grouped client-side in useStudentWorkspaceData.js, same "fetch broad list
// once, build id-map" pattern as courses/Index.jsx's stat counts.
export function getNotesForWorkspace(workspaceId) {
   return axios.get('/api/studentelementnotes?workspace=' + workspaceId, { withCredentials: true });
}

// kind is 'topicElement' | 'exerciseElement' | 'exerciseInteractiveElement' -
// exactly one of these three fields is ever set on the backend entity.
export function createTeacherNote(workspaceId, kind, targetId, teacherAuthorId, text, color) {
   const payload = {
      workspace: workspaceId,
      authorType: 'TEACHER',
      teacherAuthor: teacherAuthorId,
      type: 'STICKY',
      text,
      config: JSON.stringify({ color }),
   };
   payload[kind] = targetId;
   return axios.post('/api/studentelementnotes', JSON.stringify(payload), jsonHeaders);
}

// Every *Service.update() in this backend does a raw repository.save() of
// the request body with no prior load (confirmed convention, same class of
// bug already hit once this session with the course-visibility toggle) - so
// this always resends the FULL existing note (relation fields flattened
// back to bare ids) with just `text`/`config` overridden, never a partial
// {text, config} body that would null out workspace/target/author/type.
export function updateTeacherNote(note, text, color) {
   const idOf = (v) => (v && typeof v === 'object' ? v.id : v);
   const payload = {
      id: note.id,
      workspace: idOf(note.workspace),
      topicElement: note.topicElement ? idOf(note.topicElement) : null,
      exerciseElement: note.exerciseElement ? idOf(note.exerciseElement) : null,
      exerciseInteractiveElement: note.exerciseInteractiveElement ? idOf(note.exerciseInteractiveElement) : null,
      authorType: note.authorType,
      teacherAuthor: note.teacherAuthor ? idOf(note.teacherAuthor) : null,
      type: note.type,
      text,
      config: JSON.stringify({ color }),
   };
   return axios.put('/api/studentelementnotes/' + note.id, JSON.stringify(payload), jsonHeaders);
}

export function deleteNote(noteId) {
   return axios.delete('/api/studentelementnotes/' + noteId, { withCredentials: true });
}
